/**
 * Step-icon vocabulary for the journey map's stops — the render half of the
 * picker in `admin/src/components/icons/stepIcons.tsx`. The KEY is what admin
 * persists on `QuestConfigMilestone.icon`; this maps it to the element the
 * player card draws (`devourplay-anchor/Play/play-the-run.html` gives every
 * stop its own icon rather than an index — FaDownload for "Download & Sign In",
 * FaShieldAlt for "Build a Team", GiCrossedSwords for "Enter a Comp", …).
 *
 * Deliberately NOT shared with admin's copy: that one carries picker labels and
 * lives in a different package, and react-icons has no by-name lookup that
 * wouldn't pull the whole library into the bundle. An unknown or unset key
 * falls back rather than breaking the card, so admin can add a key ahead of web.
 */
import { ReactElement } from "react";
import {
    FaBolt,
    FaBoxOpen,
    FaBullseye,
    FaCalendarCheck,
    FaComments,
    FaCrown,
    FaDownload,
    FaFire,
    FaGamepad,
    FaGasPump,
    FaGift,
    FaShieldAlt,
    FaShoppingCart,
    FaStar,
    FaTrophy,
    FaTv,
    FaUserPlus,
} from "react-icons/fa";
import { GiCrossedSwords } from "react-icons/gi";

/*
 * Anchor-styled wheel glyph — 8 alternating wedges, center pip. Lives here
 * rather than in DailyWheelFab (which imports DailyWheelModal) so the icon
 * vocabulary stays free of heavy component deps; the FAB imports it back.
 */
export const WheelGlyph = (): ReactElement =>
    <svg viewBox="0 0 32 32" aria-hidden="true">
        <g>
            <circle cx="16" cy="16" r="14" fill="#0a0a0c" stroke="currentColor" strokeWidth="1.6" />
            {Array.from({ length: 8 }).map((_, i) => {
                const a = i / 8 * Math.PI * 2;
                return (
                    <line
                        key={i}
                        x1={16}
                        y1={16}
                        x2={16 + Math.cos(a) * 14}
                        y2={16 + Math.sin(a) * 14}
                        stroke="currentColor"
                        strokeWidth="1.2"
                        opacity={i % 2 === 0 ? 0.85 : 0.42}
                    />
                );
            })}
            <circle cx="16" cy="16" r="2.4" fill="currentColor" />
            <polygon points="16,1 18,5 14,5" fill="currentColor" />
        </g>
    </svg>;

const STEP_ICON_BY_KEY: Record<string, ReactElement> = {
    FaDownload: <FaDownload aria-hidden />,
    FaGamepad: <FaGamepad aria-hidden />,
    FaBullseye: <FaBullseye aria-hidden />,
    FaFire: <FaFire aria-hidden />,
    FaTrophy: <FaTrophy aria-hidden />,
    FaTv: <FaTv aria-hidden />,
    FaComments: <FaComments aria-hidden />,
    FaUserPlus: <FaUserPlus aria-hidden />,
    FaShieldAlt: <FaShieldAlt aria-hidden />,
    FaGift: <FaGift aria-hidden />,
    FaBoxOpen: <FaBoxOpen aria-hidden />,
    FaShoppingCart: <FaShoppingCart aria-hidden />,
    GiCrossedSwords: <GiCrossedSwords aria-hidden />,
    FaStar: <FaStar aria-hidden />,
    FaCrown: <FaCrown aria-hidden />,
    FaGasPump: <FaGasPump aria-hidden />,
    FaBolt: <FaBolt aria-hidden />,
    /* The daily spin — the real wheel, not a stand-in gift box. */
    Wheel: <WheelGlyph />,
    /*
     * Streaks. NOT FaFire: the flame is the platform's Fuel glyph
     * (CompactProfileBar renders it for the Fuel balance, ChallengeCard for
     * the Fuel entry fee), so using it for a streak misreads as a payout.
     */
    FaCalendarCheck: <FaCalendarCheck aria-hidden />,
};

/** The icon for a persisted key, or the generic goal icon when unset/unknown. */
export function stepIcon(key?: string): ReactElement {
    return key && STEP_ICON_BY_KEY[key] || <FaBullseye aria-hidden />;
}
